import { createClient } from "@liveblocks/client";
import { LiveMap } from "@liveblocks/core";
import { authorization } from "module/auth";
import { updateFlyerBuilderStates } from "graphQL/mutations/flyerBuilderStates";
import { buildLiveblocksAuthEndpoint, buildLiveblocksUserPayload } from "./liveblocksAuth";

const authEndpointUrl = process.env.REACT_APP_LIVEBLOCKS_AUTH_URL;

/**
 * Reset a guest's Liveblocks room back to "empty" on every fresh load
 * of the public builder. Four layers cooperate to make this stick:
 *
 *   1. Liveblocks `records` LiveMap — the live source of truth for
 *      the canvas. Iterated and deleted so what useMultiplayerState
 *      reads on mount is an empty room.
 *   2. MongoDB `FlyerBuilderState` (via `updateFBStatesWithoutAuth`) —
 *      the recovery snapshot. `LiveStorage.recoverFromServer` reads
 *      this when the live room is empty and would re-populate the
 *      Liveblocks room from it, undoing step 1. Overwriting it with
 *      `[]`/`[]`/`[]` (shapes/bindings/assets) — `$set`-style upsert
 *      preserves `documentSettings` so format stays put.
 *   3. localStorage `guestPlaceholdersInjected_<pathname>` — the
 *      marker that tells `injectGuestPlaceholders` whether it's
 *      already seeded this room. Removed so the placeholder front /
 *      back imagery re-injects when the editor mounts.
 *   4. localStorage orientation keys — `flyerOrientation_<designId>`,
 *      `flyerOrientation_<liveblocksRoomId>`, and
 *      `flyerOrientationPinned_<designId>`. Without these the canvas
 *      mounts with whatever orientation the previous session left
 *      (Settings.tsx + useMultiplayerState both read from
 *      localStorage on first render), so a guest who flipped to
 *      Landscape would reload into a landscape canvas with the
 *      portrait placeholder cropped weird. Wiping forces the
 *      `defaultDocument.orientation = Portrait` static back into
 *      effect on the next mount.
 *
 * Carry-over is unaffected: the post-signup redirect lands on the
 * AUTHED builder URL (`authorization.isLogged()` true by then), and
 * the caller in `PublicBuilder.js` short-circuits before invoking
 * this for authed mounts.
 *
 * Best-effort throughout — every failure is logged and swallowed so
 * a transient network blip or auth hiccup can't strand the user on a
 * blank loader.
 *
 * @param liveblocksRoomId Full Liveblocks room id from `getRoomId`
 *   (e.g. `dev_guest_<anonId>_test-room-200`).
 * @param designId URL path segment (e.g. `test-room-200`). Used to
 *   build the `_<designId>` suffixed localStorage keys that the
 *   in-builder Settings panel reads/writes.
 * @param pathnameKey `window.location.pathname` at the time of the
 *   call — used as the placeholder-injection marker key.
 */
export const clearGuestRoom = async (
  liveblocksRoomId: string,
  designId: string,
  pathnameKey: string
): Promise<void> => {
  // 1) Liveblocks records.
  if (authEndpointUrl) {
    const userPayload = buildLiveblocksUserPayload(authorization.getUser?.());
    const authEndpoint = buildLiveblocksAuthEndpoint(authEndpointUrl, userPayload);
    let leave: (() => void) | null = null;
    try {
      const liveblocks = createClient({ authEndpoint });
      const enter = liveblocks.enterRoom(liveblocksRoomId, {
        initialPresence: { presence: undefined } as any,
        initialStorage: { records: new LiveMap() } as any,
      });
      const room = (enter as any).room;
      leave = (enter as any).leave;

      const storage = await room.getStorage();
      const liveRecords = storage.root.get("records") as any;
      if (liveRecords && typeof liveRecords.keys === "function") {
        // LiveMap has no `.clear()` — materialize current keys via
        // Array.from so the iteration isn't invalidated by the
        // deletes that follow.
        for (const key of Array.from(liveRecords.keys() as IterableIterator<string>)) {
          liveRecords.delete(key);
        }
      }

      // Wait for the deletions to flush to the Liveblocks server
      // before we close the connection — otherwise the page reload
      // tears down the WebSocket while the deletes are still
      // buffered client-side and they never land.
      await new Promise<void>((resolve) => {
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          resolve();
        };
        try {
          if (typeof room.getStorageStatus === "function") {
            if (room.getStorageStatus() === "synchronized") {
              finish();
              return;
            }
            if (typeof room.subscribe === "function") {
              const unsub = room.subscribe("storage-status", (status: string) => {
                if (status === "synchronized") {
                  try {
                    unsub();
                  } catch {
                    /* ignore */
                  }
                  finish();
                }
              });
            }
          }
        } catch {
          /* fall through to timeout */
        }
        // Hard cap so a stuck sync doesn't block the page render.
        setTimeout(finish, 3000);
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[clearGuestRoom] Liveblocks clear failed:", err);
    } finally {
      if (leave) {
        try {
          leave();
        } catch {
          /* already left */
        }
      }
    }
  }

  // 2) MongoDB FlyerBuilderState — overwrite the recovery snapshot
  //    with empty shape collections. Keep `documentSettings` out of
  //    the payload so `$set` leaves the row's orientation/format
  //    fields intact (a missing key under `$set` is a no-op).
  try {
    await updateFlyerBuilderStates({
      key: liveblocksRoomId,
      states: {
        shapes: [],
        bindings: [],
        assets: [],
      } as any,
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[clearGuestRoom] FlyerBuilderState clear failed:", err);
  }

  // 3) Placeholder injection marker — keyed by URL pathname, see the
  //    matching effect in `Tldraw.tsx`. Removing it makes the next
  //    editor mount inject the front/back marketing imagery again.
  try {
    window.localStorage.removeItem(`guestPlaceholdersInjected_${pathnameKey}`);
  } catch {
    /* localStorage unavailable — injection will likely fire again
       anyway since most paths re-check the marker each mount */
  }

  // 4) Orientation keys. Two writers in the app produce these:
  //      - `Settings.tsx` writes `flyerOrientation_<designId>`
  //        (URL segment id) via `setStoredOrientation`.
  //      - `useMultiplayerState.setupDocument` writes
  //        `flyerOrientation_<liveblocksRoomId>` (full namespaced id)
  //        when it syncs from MongoDB to localStorage.
  //    Clear all three so neither code path finds a stale "Landscape"
  //    on the next mount. The pinned-flag is only used during the
  //    signup carry-over; cleaning it is defense-in-depth in case
  //    one was left behind from a previous flow.
  try {
    window.localStorage.removeItem(`flyerOrientation_${designId}`);
    window.localStorage.removeItem(`flyerOrientation_${liveblocksRoomId}`);
    window.localStorage.removeItem(`flyerOrientationPinned_${designId}`);
  } catch {
    /* localStorage unavailable — the static default in
       `TldrawApp.defaultDocument` is Portrait so the mount falls
       back to that anyway */
  }
};
